import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { memoize, nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";

import { InstitutionModel } from "@courses-models/institution";
import { api } from "@courses-models/user/src/ts/config";
import {
  UserModel,
  UserTypes,
} from "@courses-models/user/src/ts/models/UserModel";

/**
 * Describes data returned by v1/UnicUser/GetPerson from Gyldendal Access
 * Service.
 */
type v1_UnicUser_GetPerson_Response = {
  Birthdate: string | null;
  Email: string | null;
  FirstName: string;
  Function: string | null;
  InstitutionId: string;
  LastName: string;
  Name: string;
  SchoolComName: string | null;
  UserId: string;
};

/**
 * Describes data returned by v2/UnicUser/GetStudent from Gyldendal Access
 * Service.
 */
type v2_UnicUser_GetStudent_Response = {
  Birthdate: string | null;
  Class: {
    Name: string | null;
  };
  Email: string | null;
  FirstName: string;
  Function: string | null;
  InstitutionId: string;
  LastName: string;
  Name: string;
  SchoolComName: string | null;
  UserId: string;
};

/**
 * Specification of data required to instantiate a new UnicUserModel.
 */
type UnicUserData = {
  id: string;
  institutionId: string;
  mail: string | null;
  name: string;
  userType: UserTypes;
};

/**
 * Memory cache of users that have already been fetched by the client, so that
 * we don't need to fetch the same users over and over again.
 */
const cache = new EntityStore<UnicUserModel>({ node: false, ttl: 600 });

/**
 * Specification of users from the Unic identity provider, including all api
 * integrations required to load data.
 */
export class UnicUserModel extends UserModel {
  /**
   * Specifies the id of the institution that this user belongs to.
   */
  public readonly institutionId: string;

  /**
   * Specifies the type of the user (ie. either teacher or student).
   */
  public readonly userType: UserTypes;

  /**
   * Apply data initially when creating a user model.
   *
   * You shouldn't create instances using new UnicUserModel(...) directly, use
   * UnicUserModel.CREATE instead. That way we can ensure cache integrity.
   */
  constructor(data: UnicUserData) {
    super(data);
    this.institutionId = data.institutionId;
    this.userType = data.userType;

    // Cache the user model instance for future reference, so we don't need
    // to re-fetch it again later on
    cache.set(this.id, this);
  }

  /**
   * Helper that'll return a unic user model based on the given id.
   */
  @nonConcurrent()
  public static async GET_BY_ID(id: string): Promise<UnicUserModel> {
    // If this user has already been cached, then return an instance of this
    // method directly from there
    if (cache.has(id)) {
      return <UnicUserModel>cache.get(id);
    }

    // Fetch data about the user from Gyldendal Access Service
    const endpoint = `${api.gyldendalAccessService(
      "v1"
    )}/api/v1/UnicUser/GetPerson?userId=${encodeURIComponent(id)}`;
    const response = await axios.get<v1_UnicUser_GetPerson_Response>(gGateway(endpoint));

    // Determine if the user is a student or a teacher
    const userType = String(response.data.Function).toLowerCase();
    let isStudent =
      userType === "elev" ||
      userType === "student" ||
      userType === "stud" ||
      userType === "studerende" ||
      userType === "barn";

    // specifically for students, we need to check if they're studying to
    // become teachers (by checking the type of institution that they're
    // associated with), because in that case they should naturally be
    // considered to be teachers
    if (isStudent && response.data.InstitutionId) {
      const institution = await InstitutionModel.GET_BY_ID(
        response.data.InstitutionId
      );

      if (institution.isTeacherEducation) {
        isStudent = false;
      }
    }

    // Format data into well known format
    return new UnicUserModel({
      id,
      institutionId: response.data.InstitutionId,
      mail: response.data.Email,
      name: `${response.data.FirstName} ${response.data.LastName}`,
      userType: isStudent ? UserTypes.STUDENT : UserTypes.TEACHER,
    });
  }

  /**
   * Helper that'll create a new UnicUserModel based on the given data.
   */
  public static CREATE(data: UnicUserData): UnicUserModel {
    // If the model already exists in our cache, then return it directly
    // from there
    if (cache.has(data.id)) {
      return <UnicUserModel>cache.get(data.id);
    }

    // ... Otherwise create a new model
    return new UnicUserModel(data);
  }

  /**
   * Helper that'll return the user's institutionId publicly in a fashion that
   * lives up to the interface of the UserModel.
   */
  public async getInstitutionId(): Promise<string> {
    return this.institutionId;
  }

  /**
   * Helper that'll look up the user's based on his institution association.
   */
  public async getUserType(): Promise<UserTypes> {
    return this.userType;
  }

  /**
   * Helper that'll resolve the class that this user is a part of (this
   * applies only to students!).
   */
  @memoize()
  public async getClass(): Promise<string | null> {
    // Bail out for teachers
    if (this.userType !== UserTypes.STUDENT) {
      // tslint:disable-next-line: no-null-keyword
      return null;
    }

    const userId = encodeURIComponent(this.id);
    const institutionId = encodeURIComponent(this.institutionId);

    // Fetch data about the user from Gyldendal Access Service
    // tslint:disable-next-line max-line-length
    const endpoint = `${api.gyldendalAccessService(
      "v2"
    )}/api/v2/UnicUser/GetStudent?userId=${userId}&institutionNumber=${institutionId}`;
    const response = await axios.get<v2_UnicUser_GetStudent_Response>(gGateway(endpoint));

    // Resolve user class
    return response.data.Class.Name;
  }

  /**
   * Allow easy debugging by providing a toString helper.
   */
  public toString(): string {
    return `[UnicUserModel ${this.id}]`;
  }
}
